import { useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLocation, Link } from "react-router-dom";
import { NAV } from "@/constants/testIds";

const LINKS = [
  { label: "Portfolio", kind: "route", to: "/portfolio", id: NAV.portfolio },
  { label: "Focus", kind: "anchor", anchor: "focus", id: NAV.focus },
  { label: "Contact", kind: "route", to: "/contact", id: NAV.contact },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const onHome = location.pathname === "/";
  const hrefFor = (anchor) => (onHome ? `#${anchor}` : `/#${anchor}`);

  const { scrollY } = useScroll();
  // Transparent at hero → fully solid warm-white once user scrolls past ~60px.
  const bg = useTransform(scrollY, [0, 60], ["rgba(247,246,243,0)", "rgba(247,246,243,1)"]);
  const borderColor = useTransform(scrollY, [0, 60], ["rgba(224,221,214,0)", "rgba(224,221,214,1)"]);
  const shadow = useTransform(
    scrollY,
    [0, 60],
    ["0 0 0 rgba(0,0,0,0)", "0 4px 20px -8px rgba(19,98,7,0.08)"]
  );

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const renderLink = (l, onClick, mobile = false) => {
    const base = mobile
      ? "text-[#1A1A1A] text-2xl font-display"
      : "text-[12px] uppercase tracking-[0.22em] text-[#5C5A55] hover:text-[#136207] transition-colors";
    const testId = mobile ? `${l.id}-mobile` : l.id;
    if (l.kind === "route") {
      return (
        <Link key={`${l.to}-${mobile}`} to={l.to} data-testid={testId} onClick={onClick} className={base}>
          {l.label}
        </Link>
      );
    }
    return (
      <a key={`${l.anchor}-${mobile}`} href={hrefFor(l.anchor)} data-testid={testId} onClick={onClick} className={base}>
        {l.label}
      </a>
    );
  };

  return (
    <motion.header
      style={{ backgroundColor: bg, borderBottomColor: borderColor, boxShadow: shadow }}
      className="fixed top-0 inset-x-0 z-50 border-b"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid={NAV.brand} className="flex items-center gap-3 group">
          <span className="flex items-end gap-[3px]" aria-hidden>
            <span className="block w-[3px] h-7 bg-[#136207] transition-all duration-300 group-hover:h-8" />
            <span className="block w-[3px] h-5 bg-[#136207] transition-all duration-300 group-hover:h-6" />
            <span className="block w-[3px] h-3 bg-[#136207] transition-all duration-300 group-hover:h-4" />
          </span>
          <span className="text-[#1A1A1A] tracking-[0.18em] text-[13px] uppercase font-medium">
            Meezan <span className="text-[#5C5A55]">Ventures</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {LINKS.map((l) => renderLink(l))}
        </nav>

        <button
          data-testid={NAV.menuToggle}
          onClick={() => setOpen((v) => !v)}
          className="md:hidden text-[#1A1A1A] p-2 -mr-2"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-[#F7F6F3] border-t border-[#E0DDD6]">
          <div className="px-6 py-8 flex flex-col gap-6">
            {LINKS.map((l) => renderLink(l, () => setOpen(false), true))}
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              className="mt-4 inline-flex items-center justify-center gap-2 px-5 py-4 bg-[#136207] text-white text-[11px] uppercase tracking-[0.22em]"
            >
              Get in touch
            </Link>
          </div>
        </div>
      )}
    </motion.header>
  );
};

export default Navbar;
